import {useEffect, useMemo, useRef, useState} from 'react';
import {MoreType} from '@/domains/space/Repository/SidebarNew/sidebarTreeUtils';
import {canEditInKnowledge} from '@/domains/space/Repository/knowledgeUtils';
import {BOOK_PUBLIC_TYPE, BOOK_TYPE, DOC_TYPE} from '@/constants/document/doc';
import * as store from '@/store';
import toast, {ToastType} from '@/components/Toast';
import {useIntl} from 'react-intl';
import {TreeNodeData} from '@/domains/space/Repository/SidebarNew/type';
import {addToCommonDoc, removeCommonDoc} from '@/domains/space/Repository/SidebarNew/CommonDoc/utils';
import eventBus, {ADD_COMMON_DOC_SUCCESS, REMOVE_COMMON_DOC_SUCCESS} from '@/eventBus';
import {Menu, MenuItem, MenuPopper, Submenu} from '@baidu/luffy-comp-menu';
import KuAuthority from '@/auth/KuAuthority';
import {getNodeCanEdit, getNodeById, addBlur} from '../../sidebarTreeUtils';
import styles from './index.module.scss';
import Icon, {IconSize} from '@/components/Icon';
import Popover from '@/components/Popover';
import Text, {TextType} from '@/components/Text';
import Tooltip from '@/components/Tooltip';
import {
    SidebarTeamSvg,
    SidebarPagePublicSvg,
    BookArrowRightSvg,
    HelpBookSvg,
} from '@/icons';
import classNames from 'classnames';
import SidebarBookPublicSvg from '@/icons/SidebarBookCloseSvg';
import arrowDownSvg from '@/assets/svg/arrowDownSvg.svg?tsx';
import {InheritRepo} from '@/Constant';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {
    clickNewKnowledgeBook,
    clickSwitchNewKnowledgeBook,
    clickMoveToTop,
    clickMoveToBottom,
    feLogger,
} from '@/buried';
import sdk from '@/sdk';
import {isCurDocPageAdmin, isCurDocPageMember, isMember, isMemberRepository} from '@/store/util';
import {calCanShowPublish} from '@/utils/document/calCanShowPublish';
import {
    findSiblingListAndIndex,
    isFirstItem,
    isLastItem,
    quickMoveToTopOrBottom,
} from '../../sidebarTreeUtils/positionUtils';
import {isInKuShare} from '@/utils/urlUtils';
import kuPiper from '@/middleware/kuPiper';

const OPENBOOK_TYPE = 5;
const KnowledgeBookDetails = 'https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/2tsPs8CtSd/Jw3ESeeIzA/wX6S4XS3VZk0xr';

const renderOptions = (options?: any[]) => {
    if (!Array.isArray(options)) {
        return null;
    }

    return options.map((opt: any, index) => {
        const {children, value, label} = opt;

        if (!children) {
            return (
                <MenuItem
                    key={value}
                    id={opt.value}
                    disabled={opt.disabled}
                    onClick={opt.onClick}
                    data-divider={opt.divider ? 'true' : undefined}
                    data-separator={opt.separator ? 'true' : undefined}
                >
                    {label}
                </MenuItem>
            );
        }
        return (
            <Submenu
                key={`${opt.value}-sub`}
                itemContent={opt.label}
                type={'group'}
                disabled={opt.disabled}
                popperTriggerType={opt.popperTriggerType}
            >
                {renderOptions(children)}
            </Submenu>
        );
    });
};

// eslint-disable-next-line max-statements
function SidebarMoreMenu(props: any) {
    const {sidebarMenuTarget, treeData} = props;
    const node = sidebarMenuTarget?.node || {};
    const intl = useIntl();
    const [visible, setVisible] = useState(false);

    const {isAdmin: isReposAdmin, isPersonalMyself} = KuAuthority.getAuthRepository();
    const [moreOptions, setMoreOptions] = useState<any>([]);
    const canEdit = getNodeCanEdit(node);
    const repository = store.getCurRepository();
    const isRepoEdit = canEditInKnowledge(repository, node?.bookType, node?.inheritRepo);
    const isChangeType = node?.bookType === BOOK_TYPE.IN_BOOK || node?.bookType === BOOK_TYPE.IS_BOOK;
    const switchBook = node?.bookType === BOOK_TYPE.IS_BOOK;
    const isPop = !switchBook;
    const isDocAdmin = isCurDocPageAdmin();
    // 权限不够或者知识本以及知识本以下的文档
    const canUpdatePagePublic = calCanShowPublish(repository as Repository, sdk.as.user!.userGuid, !isReposAdmin);
    // 是否ku享模式
    const isKuShare = isInKuShare();

    // 知识库成员 编辑者
    const isMemberEditor = isMemberRepository() && isCurDocPageMember();

    // 知识本点击查看 使用手册
    const handleClickKnowledgeBookDetails = () => {
        window.open(KnowledgeBookDetails);
    };

    const needBlur = useRef(true);

    function handleImportMouseOver() {
        needBlur.current = false;
    }

    function handleImportMouseLeave() {
        needBlur.current = true;
    }

    const baseLogParams = {
        docGuid: node?.docGuid || '',
        repository_id: node?.repositoryGuid || repository?.repositoryGuid || '',
        userId: sdk.as.user?.userGuid || '',
        CreateCource: !isChangeType ? 'RegularPageChange' : 'RootTreeChange',
    };
    const logBookClick = (BookType: 'RegularBook' | 'OpenBook' | 'SafeBook') => {
        const logParams = {
            ...baseLogParams,
            BookType,
        };
        if (isChangeType) {
            feLogger(clickSwitchNewKnowledgeBook(logParams));
        } else {
            feLogger(clickNewKnowledgeBook(logParams));
        }
    };

    const bookTypesMap = [
        {
            key: 'ordinary',
            labelId: 'labelOrdinaryKnowledge',
            descId: 'labelOrdinaryKnowledgeText',
            icon: SidebarTeamSvg,
            onClick: () => {
                store.setConvertToCatalogDocument(`${node.docGuid}`, undefined, undefined, !!isPop);
                logBookClick('RegularBook');
            },
            showCheck: switchBook &&
                !(node?.scope === BOOK_PUBLIC_TYPE.isReaderPublic ||
                    node?.scope === BOOK_PUBLIC_TYPE.isMemberPublic) &&
                node?.inheritRepo === InheritRepo.Inherit,
        },
        {
            key: 'open',
            labelId: 'labelOpenKnowledge',
            descId: 'labelOpenKnowledgeText',
            icon: SidebarPagePublicSvg,
            onClick: () => {
                !isChangeType ?
                    store.setConvertToCatalogDocument(`${node.docGuid}`, InheritRepo.Inherit, OPENBOOK_TYPE, !!isPop) :
                    store.setConvertToCatalogDocument(`${node.docGuid}`, undefined, OPENBOOK_TYPE, !!isPop);
                logBookClick('OpenBook');
            },
            showCheck: switchBook &&
                (node?.scope === BOOK_PUBLIC_TYPE.isReaderPublic ||
                    node?.scope === BOOK_PUBLIC_TYPE.isMemberPublic),
        },
        {
            key: 'secrecy',
            labelId: 'labelSecrecyKnowledge',
            descId: 'labelSecrecyKnowledgeText',
            icon: SidebarBookPublicSvg,
            onClick: () => {
                store.setConvertToCatalogDocument(`${node.docGuid}`, InheritRepo.Not_Inherit, undefined, !!isPop);
                logBookClick('SafeBook');
            },
            showCheck: switchBook &&
                !(node?.scope === BOOK_PUBLIC_TYPE.isReaderPublic ||
                    node?.scope === BOOK_PUBLIC_TYPE.isMemberPublic) &&
                node?.inheritRepo === InheritRepo.Not_Inherit,
        },
    ];

    const bookTypes = kuPiper.openListSlot(
        {
            scope: kuPiper.slotScopeMap.SpaceRepo,
            position: kuPiper.slotPositionMap.SpaceRepo.ConvertBookTypeMenu,
            originList: bookTypesMap,
        }
    );

    const bookSubmenu = (
        <div
            className={styles.importBookSubmenu}
            onMouseOver={handleImportMouseOver}
            onMouseLeave={handleImportMouseLeave}
        >
            {bookTypes.map(({key, labelId, descId, icon, onClick, showCheck}) => (
                <div
                    key={key}
                    className={styles.item}
                    onClick={() => onClick()}
                >
                    <Icon
                        svg={icon}
                        size={IconSize.XXL}
                        className={classNames(styles.bookSubmenuIcon, showCheck && styles.selectedIcon)}
                        noSpace
                    />
                    <div className={classNames(styles.itemContent, showCheck && styles.selectedContent)}>
                        <Text type={TextType.T13} className={styles.text}>{intl.formatMessage({id: labelId})}</Text>
                        <p title={intl.formatMessage({id: descId})}>{intl.formatMessage({id: descId})}</p>
                    </div>
                    {showCheck && <Icon svg={arrowDownSvg} size={IconSize.XS} className={styles.checkIcon} />}
                </div>
            ))}
        </div>
    );

    const DirectoryPopover = () => {
        return (
            <Popover
                className={classNames(styles.importOneLine, {[styles.isChangeTypeActive]: isChangeType})}
                type={'hover'}
                placement={'left-start'}
                withArrow={false}
                content={bookSubmenu}
            >
                <div className={styles.oneLineWarp}>
                    <div className={classNames(styles.oneLineLeft, {[styles.isChangeTypeLeft]: !isChangeType})}>
                        <div>
                            {
                                isChangeType ?
                                    intl.formatMessage({id: 'labelChangeKnowledgeType'}) :
                                    intl.formatMessage({id: 'indexMenuConvertToknowledgeBasis'})
                            }
                        </div>
                        <Tooltip
                            placement="top"
                            className={styles.tip}
                            content={
                                <>
                                    用于在知识库内单独管理权限。详细介绍请点击&nbsp;
                                    <span
                                        onMouseDown={handleClickKnowledgeBookDetails}
                                        className={styles.link}
                                    >前往查看</span>
                                </>
                            }
                        >
                            <Icon svg={HelpBookSvg} className={styles.helpSvg} />
                        </Tooltip>
                    </div>
                    <Icon
                        svg={BookArrowRightSvg}
                        className={styles.arrow}
                        noSpace
                    />
                </div>
            </Popover>
        );
    };

    const {siblingList, index} = useMemo(() => {
        if (!node?.docGuid) {
            return {siblingList: [], index: -1};
        }
        return findSiblingListAndIndex(treeData, node.docGuid);
    }, [treeData, node?.docGuid]);

    const isFirst = isFirstItem(siblingList, index);
    const isLast = isLastItem(siblingList, index);
    useEffect(() => {
        let options: any = [
            {
                label: <div>{intl.formatMessage({id: 'indexMenuRename'})}</div>,
                value: MoreType.Rename,
                disabled: false,
                // 有权限/在知识本中
                divider: (isReposAdmin || node?.bookType === BOOK_TYPE.IN_BOOK ||
                    (isDocAdmin && node?.bookType === BOOK_TYPE.IS_BOOK) || isMember()) && true,
            },
            {
                label: <div>{intl.formatMessage({id: 'indexMenuCopy'})}</div>,
                value: MoreType.Copy,
                disabled: false,
            },
        ];

        if (isPersonalMyself) {
            const isCommonDoc = node?.active;
            options.push({
                label: <div>{intl.formatMessage({
                    id: isCommonDoc ? 'removeCommonDoc' : 'addCommonDoc',
                })}</div>,
                value: isCommonDoc ? MoreType.RemoveCommonDoc : MoreType.AddCommonDoc,
                disabled: false,
                divider: isInKuShare(), // ku享不展示移动到顶部/底部,需在常用页面下加分割线
            });
        }

        // 移动到顶部/底部
        // 在知识本中有可编辑及以上权限展示入口，知识库管理员/成员展示入口 isKuShare不支持
        if (!isKuShare && (
            (node?.bookType === BOOK_TYPE.IN_BOOK && (isDocAdmin || isCurDocPageMember())) ||
            isMember()
        )) {
            options.push({
                label: <div>{intl.formatMessage({id: 'indexMenuQuickMoveTop'})}</div>,
                value: MoreType.QuickMoveTop,
                disabled: isFirst,
            });
            options.push({
                label: <div>{intl.formatMessage({id: 'indexMenuQuickMoveBottom'})}</div>,
                value: MoreType.QuickMoveBottom,
                disabled: isLast,
                divider: !(isMemberEditor &&
                        node?.inheritRepo === InheritRepo.Not_Inherit &&
                        node?.bookType === BOOK_TYPE.IS_BOOK) && true,
            });
        }

        // 非个人知识库页面管理员需要展示变更类型入口，个人知识库仅库管理员
        if (
            node?.type === DOC_TYPE.DIRECTORY &&
            (
                isReposAdmin ||
                (canUpdatePagePublic && !isReposAdmin && isDocAdmin)
            )
        ) {
            const bookType = node?.bookType;
            if (bookType && bookType !== BOOK_TYPE.IN_BOOK) {
                options.push({
                    children: [
                        {
                            label: (
                                <DirectoryPopover />
                            ),
                            value: 'DirectoryPopover',
                            disabled: false,
                        },
                    ],
                    value: MoreType.ConvertToCatalog,
                    // disabled: false,
                });
            }
        }

        if (isReposAdmin && (node?.type === DOC_TYPE.DOCUMENT || node?.type === DOC_TYPE.DIRECTORY)) {
            const bookType = node?.bookType;
            if (bookType && bookType !== BOOK_TYPE.IN_BOOK) {
                options.push({
                    label: bookType === BOOK_TYPE.NO_BOOK ? (
                        <div>
                            {[
                                {
                                    label: <DirectoryPopover />,
                                    value: 'DirectoryPopover',
                                    disabled: false,
                                },
                            ].map((option) => (
                                <div key={option.value}>
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div>{intl.formatMessage({id: 'indexMenuConvertToDoc'})}</div>
                    ),
                    value: bookType === BOOK_TYPE.NO_BOOK ? MoreType.ConvertToCatalog : MoreType.ConvertToDoc,
                    disabled: !canEdit,
                    divider: true,
                });
            }
        }

        options.push({
            label: <div>{intl.formatMessage({id: 'indexMenuDelete'})}</div>,
            value: MoreType.Delete,
            disabled: false,
            separator: isMemberRepository() && isDocAdmin && node?.bookType === BOOK_TYPE.IS_BOOK,
        });

        const directoryEdit = store.getDirectoryEdit();
        if (!isRepoEdit && directoryEdit && directoryEdit.includes(node?.docGuid)) {
            options = options.filter((option: any) => {
                if ([MoreType.Copy, MoreType.Delete].includes(option.value)) {
                    return false;
                }
                return true;
            });
        }

        setMoreOptions(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        canEdit,
        isRepoEdit,
        isReposAdmin,
        sidebarMenuTarget,
    ]);

    function nodeContentCallback(type: string, node: TreeNodeData) {
        if (type === MoreType.Copy) {
            const type = node.type; // 1: 文档  4:表格
            type && store.setCopyDocument(type, `${node.docGuid}`, node.name || '', true, !node.isLeaf, false);
        }
        if (type === MoreType.Move) {
            store.setMoveDocument(`${node.docGuid}`);
        }
        if (type === MoreType.ConvertToDoc) {
            store.setConvertToDocDocument(`${node.docGuid}`);
        }
        if (type === MoreType.AddCommonDoc) {
            if (node.docGuid && repository) {
                const docGuid = String(node.docGuid);
                addToCommonDoc(docGuid).then(() => {
                    eventBus.emit(ADD_COMMON_DOC_SUCCESS);
                    const {
                        value: treeData,
                        setValue: setTreeData,
                    } = store.getSidebarTreeObj();
                    const curNode = getNodeById(treeData, docGuid);
                    if (curNode) {
                        curNode.active = true;
                        setTreeData([...treeData]);
                    }
                });
            }
        }
        if (type === MoreType.RemoveCommonDoc) {
            if (node.docGuid && repository) {
                const docGuid = String(node.docGuid);
                removeCommonDoc(String(node.docGuid)).then(() => {
                    eventBus.emit(REMOVE_COMMON_DOC_SUCCESS);
                    const {
                        value: treeData,
                        setValue: setTreeData,
                    } = store.getSidebarTreeObj();
                    const curNode = getNodeById(treeData, docGuid);
                    if (curNode) {
                        curNode.active = false;
                        setTreeData([...treeData]);
                    }
                });
            }
        }
        if (type === MoreType.QuickMoveTop || type === MoreType.QuickMoveBottom) {
            if (node.docGuid && repository) {
                const docGuid = String(node.docGuid);
                const isTop = type === MoreType.QuickMoveTop;
                quickMoveToTopOrBottom(docGuid, isTop);
                const eventParams = {
                    userId: sdk.as.user?.userGuid || '',
                    docId: docGuid,
                };
                const eventLogger = isTop ? clickMoveToTop : clickMoveToBottom;
                // 执行埋点
                feLogger(eventLogger(eventParams));
            }
        }
    }

    function handleClickMore(value: any) {
        switch (value) {
            case MoreType.Delete:
                store.setDeleteDocument(node.docGuid, node);
                break;
            case MoreType.Rename:
                sidebarMenuTarget?.actions?.rename();
                break;
            case MoreType.Export:
                toast({
                    content: '等待 RTE 的好消息......',
                    type: ToastType.Success,
                });
                break;
            default:
                nodeContentCallback(value, node);
        }
        setVisible(false);
        store.setSidebarMenuTarget(null);
    }

    function stopEvent(e: any) {
        e.stopPropagation();
        e.preventDefault();
    }

    function handleMoreClick(e: any) {
        stopEvent(e);
    }

    function handleDropMenuPopperVisible() {
        console.log('handleDropMenuPopperVisible-----');
    }

    useEffect(() => {
        if (sidebarMenuTarget) {
            setVisible(true);
        }
    }, [sidebarMenuTarget]);

    function handleChange(val: boolean) {
        setVisible(val);
        store.setSidebarMenuTarget(null);
    }

    if (!sidebarMenuTarget || sidebarMenuTarget.type !== 'more') {
        return null;
    }

    function blurMenu() {
        if (!needBlur.current) {
            return;
        }
        handleChange(false);
    }

    const getMoreMenuRef = (ref: any) => {
        if (!ref) {
            return;
        }
        addBlur('.sidebar-more-menu', blurMenu);
    };

    return (
        <MenuPopper
            visible={visible}
            target={sidebarMenuTarget.element}
            className={styles.popperWrap}
            placement={'bottom-end'}
            withArrow={false}
            onChange={handleChange}
            type={'click'}
        >
            <Menu
                onSelect={handleClickMore}
                className="sidebar-more-menu"
                ref={getMoreMenuRef}
                type={'common'}
                submenuPopperTriggerType={'hover'}
            >
                {
                    renderOptions(moreOptions)
                }
            </Menu>
        </MenuPopper>
    );
}

export default store.connect([
    store.KEYS.SIDEBAR_MENU_TARGET,
    store.KEYS.TREE_DATA,
])(SidebarMoreMenu);
